import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useCreatePurchaseReturnMutation } from '@/features/slice/purchase-return';
import { useLazyGetPurchaseDetailsByIdQuery } from '@/features/slice/purchases';
import { toastError } from '@/utils';

import { IwFormRef } from '../../../../components/form';
import { TCreatePurchaseReturnForm } from './create-purchase-return.schema';

export const useCreatePurchaseReturn = () => {
    const [urlSearchParams] = useSearchParams();

    const purchaseIdFromUrl = urlSearchParams.get('purchase_id') || '';

    const [selectedPurchaseId, setSelectedPurchaseId] =
        useState(purchaseIdFromUrl);

    const [createPurchaseReturn, { isLoading: isSubmitting }] =
        useCreatePurchaseReturnMutation();

    const formRef = useRef<IwFormRef<TCreatePurchaseReturnForm>>(null);

    const handleSubmit = async (values: TCreatePurchaseReturnForm) => {
        const selectedItems = values.items.filter((item) => item.is_selected);

        const total_price = selectedItems.reduce(
            (total, item) => total + item.return_quantity * item.price,
            0,
        );

        const payload = {
            items: selectedItems.map((item) => ({
                purchase_item_id: item.purchase_item_id,
                product_variation_id: item.product_variation_id,
                product_name: item.purchase_item_name,
                quantity: item.return_quantity,
                price: item.price,
            })),
            reason: values.reason,
            purchase_id: selectedPurchaseId,
            total_price: total_price,
        };

        try {
            await createPurchaseReturn(payload).unwrap();
            toast.success('Purchase return created successfully');
            formRef.current?.reset();
            setSelectedPurchaseId('');
        } catch (error) {
            toastError(error, 'useCreatePurchaseReturn: handleSubmit');
        }
    };

    const [
        getPurchaseDetails,
        {
            isLoading: isPurchaseDetailsLoading,
            isFetching: isPurchaseDetailsFetching,
            data,
            isError: isPurchaseDetailsError,
            error: purchaseDetailsError,
        },
    ] = useLazyGetPurchaseDetailsByIdQuery();

    return {
        handleSubmit,
        selectedPurchaseId,
        setSelectedPurchaseId,

        getPurchaseDetails,
        isPurchaseDetailsLoading,
        isPurchaseDetailsFetching,
        data,
        isPurchaseDetailsError,
        purchaseDetailsError,

        formRef,
        isSubmitting,
    };
};
